import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, BadgeCheck } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const CREDS = [
  "Licensed CHA & freight network across Indian ports, LCS and ICDs",
  "DGFT, GSTN & MCA filings handled end-to-end by in-house specialists",
  "Situated at the Indo-Bhutan trade corridor — Birpara, Alipurduar",
];

export default function About() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section id="about" ref={ref} data-testid="about-section" className="relative py-24 sm:py-32 bg-[#0A0E17] overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
        <motion.div
          initial={{ opacity: 0, x: -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="relative order-2 lg:order-1"
        >
          <div className="relative overflow-hidden rounded-md border border-white/10 aspect-[4/5] max-w-md">
            <motion.img
              src="https://images.unsplash.com/photo-1578575437130-527eed3abbec?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
              alt="Ships at port dock"
              style={{ y: imgY }}
              className="w-full h-[116%] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0E17]/70 to-transparent" />
            <div className="absolute bottom-4 left-4 font-mono2 text-[10px] uppercase tracking-[0.25em] text-amber-400 bg-[#0A0E17]/70 backdrop-blur px-3 py-2 rounded-sm">
              Docs Verified · Cleared
            </div>
          </div>
          <div className="absolute -bottom-6 -right-2 sm:right-6 bg-[#F59E0B] text-[#0A0E17] rounded-md p-5 max-w-[220px] rotate-[-2deg] shadow-2xl">
            <BadgeCheck size={22} strokeWidth={2} />
            <p className="mt-2 font-display font-bold uppercase text-sm leading-tight">Registered trade &amp; compliance house</p>
            <p className="mt-1 font-mono2 text-[10px] tracking-wider">EST. BIRPARA — WB, INDIA</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
          className="order-1 lg:order-2"
        >
          <p className="font-mono2 text-xs uppercase tracking-[0.3em] text-amber-400 mb-4">
            The Firm
          </p>
          <h2 className="font-display font-bold tracking-tight uppercase text-2xl sm:text-3xl lg:text-5xl text-slate-100 leading-tight">
            From a tea garden town, <span className="text-[#F59E0B]">to every port in India.</span>
          </h2>
          <p className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed">
            SHIVIN INFOTECH operates from Ramjhora Tea Garden, Birpara — a
            crossroads of the Indo-Bhutan trade corridor. We combine ground-level
            border expertise with national reach: clearing cargo, booking freight,
            advising importers &amp; exporters, and keeping companies compliant
            from incorporation to annual filings.
          </p>
          <ul className="mt-8 space-y-4">
            {CREDS.map((c, i) => (
              <motion.li
                key={c}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 + i * 0.12, ease: EASE }}
                className="flex items-start gap-4 border-l-2 border-[#F59E0B]/60 pl-4"
              >
                <span className="font-mono2 text-xs text-[#F59E0B] mt-0.5">0{i + 1}</span>
                <span className="text-sm sm:text-base text-slate-300 leading-relaxed">{c}</span>
              </motion.li>
            ))}
          </ul>
          <div data-testid="registered-office-address" className="mt-10 flex items-start gap-3 bg-[#161F33] border border-white/[0.08] rounded-md p-5">
            <MapPin size={18} className="text-[#F59E0B] mt-0.5 shrink-0" />
            <p className="font-mono2 text-xs leading-relaxed text-slate-300 uppercase tracking-wider">
              Registered Office — Ramjhora Tea Garden, Prem Nagar, P.O. Ramjhora,
              P.S. Birpara, Dist. Alipurduar, PIN 735228
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
